import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Heart, Download } from 'lucide-react';
import { musicService } from '../services/musicService';
import { authService } from '../services/authService';
import { toast } from 'sonner';

const AlbumDetail = ({ onPlaySong }) => {
  const { id } = useParams();
  const [album, setAlbum] = useState(null);
  const [albumSongs, setAlbumSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({ songs: [], albums: [] });
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const albumData = await musicService.getAlbum(id);
      setAlbum(albumData);

      // Get songs for this album
      const allSongs = await musicService.getSongs();
      const filteredSongs = allSongs.filter(song => song.album_id === id);
      setAlbumSongs(filteredSongs);

      // Load favorites if authenticated
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          const favoritesData = await musicService.getFavorites();
          setFavorites({
            songs: favoritesData.songs.map(s => s.id),
            albums: favoritesData.albums.map(a => a.id)
          });
        } catch (error) {
          console.error('Error loading favorites:', error);
        }
      }
    } catch (error) {
      console.error('Error loading album:', error);
      toast.error('Error al cargar el álbum');
    } finally {
      setLoading(false);
    }
  };

  const toggleAlbumFavorite = async () => {
    if (!isAuthenticated) {
      toast.info('Inicia sesión para agregar favoritos');
      return;
    }

    try {
      const isLiked = favorites.albums.includes(id);
      
      if (isLiked) {
        await musicService.removeFromFavorites('albums', id);
        setFavorites(prev => ({
          ...prev,
          albums: prev.albums.filter(albumId => albumId !== id)
        }));
        toast.success('Álbum eliminado de favoritos');
      } else {
        await musicService.addToFavorites('albums', id);
        setFavorites(prev => ({
          ...prev,
          albums: [...prev.albums, id]
        }));
        toast.success('Álbum agregado a favoritos');
      }
    } catch (error) {
      console.error('Error toggling album favorite:', error);
      toast.error('Error al actualizar favoritos');
    }
  };

  const toggleSongFavorite = async (songId, e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.info('Inicia sesión para agregar favoritos');
      return;
    }

    try {
      const isLiked = favorites.songs.includes(songId);
      
      if (isLiked) {
        await musicService.removeFromFavorites('songs', songId);
        setFavorites(prev => ({
          ...prev,
          songs: prev.songs.filter(id => id !== songId)
        }));
        toast.success('Canción eliminada de favoritos');
      } else {
        await musicService.addToFavorites('songs', songId);
        setFavorites(prev => ({
          ...prev,
          songs: [...prev.songs, songId]
        }));
        toast.success('Canción agregada a favoritos');
      }
    } catch (error) {
      console.error('Error toggling song favorite:', error);
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleDownloadSong = async (song, e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.info('Inicia sesión para descargar canciones');
      return;
    }

    try {
      toast.info('Descargando canción...');
      
      // Crear un enlace temporal para descargar
      const link = document.createElement('a');
      link.href = song.audio_url;
      link.download = `${song.artist_name} - ${song.title}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Descargando "${song.title}"`);
    } catch (error) {
      console.error('Error downloading song:', error);
      toast.error('Error al descargar la canción');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#00CCFF] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Cargando álbum...</p>
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <p className="text-gray-500 text-lg font-medium">Álbum no encontrado</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-32 md:pb-24 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Album Header */}
      <div className="relative bg-gradient-to-r from-[#00CCFF] via-[#00CCFF]/90 to-[#056A84] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="relative px-4 md:px-8 pt-12 md:pt-20 pb-6 md:pb-8">
          <div className="flex flex-col md:flex-row items-center md:items-end gap-4 md:gap-8">
            <img
              src={album.cover}
              alt={album.title}
              className="w-40 h-40 md:w-64 md:h-64 rounded-2xl shadow-2xl object-cover transform transition-transform duration-300 hover:scale-105"
            />
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs md:text-sm font-bold text-white uppercase mb-2 tracking-wider">Álbum</p>
              <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-lg">{album.title}</h1>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 md:gap-3 text-xs md:text-sm text-white font-medium">
                <span className="font-bold">{album.artist_name}</span>
                <span className="hidden md:inline">•</span>
                <span>{album.release_year}</span>
                <span className="hidden md:inline">•</span>
                <span>{albumSongs.length} canciones</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-gradient-to-b from-white/50 to-transparent px-4 md:px-8 py-4 md:py-6">
        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={() => albumSongs.length > 0 && onPlaySong(albumSongs[0], albumSongs)}
            className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-r from-[#00CCFF] to-[#056A84] rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-xl"
          >
            <Play size={20} className="md:w-7 md:h-7 text-white ml-0.5 md:ml-1" fill="white" />
          </button>
          <button 
            onClick={toggleAlbumFavorite}
            className={`transition-all duration-300 hover:scale-110 ${
              favorites.albums.includes(id) ? 'text-[#00CCFF]' : 'text-gray-600 hover:text-[#00CCFF]'
            }`}
          >
            <Heart 
              size={28} 
              className="md:w-9 md:h-9"
              fill={favorites.albums.includes(id) ? '#00CCFF' : 'none'}
            />
          </button>
        </div>
      </div>

      {/* Track List */}
      <div className="px-4 md:px-8 pb-8">
        {/* Header - Only visible on desktop */}
        <div className="mb-4 hidden md:block">
          <div className="grid grid-cols-[40px_1fr_80px_60px_60px] gap-4 px-4 py-3 border-b border-gray-300 text-sm text-gray-600 uppercase font-bold">
            <span>#</span>
            <span>Título</span>
            <span className="text-center">Duración</span>
            <span></span>
            <span></span>
          </div>
        </div>

        <div className="space-y-2">
          {albumSongs.map((song, index) => (
            <div
              key={song.id}
              onClick={() => onPlaySong(song, albumSongs)}
              className="grid grid-cols-[40px_1fr_60px_60px] md:grid-cols-[40px_1fr_80px_60px_60px] gap-2 md:gap-4 px-2 md:px-4 py-3 rounded-xl hover:bg-white transition-all duration-300 cursor-pointer group shadow-sm hover:shadow-md"
            >
              <div className="flex items-center">
                <span className="text-gray-500 group-hover:hidden font-medium text-sm md:text-base">{index + 1}</span>
                <Play
                  size={16}
                  className="hidden group-hover:block text-[#00CCFF] md:w-5 md:h-5"
                  fill="#00CCFF"
                />
              </div>
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <img src={song.cover || album.cover} alt={song.title} className="w-10 h-10 md:w-12 md:h-12 rounded-lg shadow-md" />
                <div className="min-w-0">
                  <p className="text-gray-900 font-bold truncate text-sm md:text-base">{song.title}</p>
                  <p className="text-xs md:text-sm text-gray-500 truncate">{song.artist_name}</p>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <span className="text-gray-500 text-xs md:text-sm font-medium">{song.duration}</span>
              </div>
              <div className="flex items-center justify-center">
                <button
                  onClick={(e) => toggleSongFavorite(song.id, e)}
                  className={`transition-all duration-300 hover:scale-110 p-1 ${
                    favorites.songs.includes(song.id) ? 'text-[#00CCFF]' : 'text-gray-400 hover:text-[#00CCFF]'
                  }`}
                >
                  <Heart 
                    size={18}
                    className="md:w-5 md:h-5"
                    fill={favorites.songs.includes(song.id) ? '#00CCFF' : 'none'}
                  />
                </button>
              </div>
              <div className="flex items-center justify-center">
                <button
                  onClick={(e) => handleDownloadSong(song, e)}
                  className={`transition-all duration-300 hover:scale-110 p-1 ${
                    isAuthenticated ? 'text-gray-400 hover:text-[#00CCFF]' : 'text-gray-300 cursor-not-allowed'
                  }`}
                  disabled={!isAuthenticated}
                  title={isAuthenticated ? 'Descargar canción' : 'Inicia sesión para descargar'}
                >
                  <Download 
                    size={18}
                    className="md:w-5 md:h-5"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlbumDetail;
